import { readVersionName } from './build-selected-solution';

/** The subset of a `SolutionVersion` needed to resolve its Pro Studio row key. */
export type VersionPsCandidate = {
  id?: string | null;
  name?: string | null;
  botSolutionVersionId?: string | null;
};

/** A `solution_versions_ps` row, reduced to what decides the key form. */
export type VersionPsRow = {
  solutionVersionId?: string | null;
  isUsingUpdatedId?: boolean | null;
};

/**
 * The two forms the primary key of `tenancy_solution_versions_ps` can take.
 *
 * The key format changed generation with the Agent Manager migration and both are live:
 *
 * - **`updated`** — the `SolutionVersion` uuid. Every row created after the migration.
 * - **`legacy`** — the DASH-joined `<customer>-<bot>-<version>` composite (note: dashes,
 *   unlike the DOT-joined `botSolutionVersionId` on the version itself).
 *
 * Which one a given row uses is not guessable from the version: it is recorded on the row
 * as `isUsingUpdatedId` (default `false`). A legacy row's `versionId` is null, so it
 * cannot be looked up by uuid either — the only way to find out is to ask for both keys
 * and see which row comes back. Hence "candidates": build both, then resolve.
 *
 * The legacy composite comes from the version's `botSolutionVersionId` whenever it has
 * one, exactly as the backend keys these rows (`toPsSolutionVersionId` in
 * `bot-migration/sync-bots.ts`). Not from the version's current name: the backend's
 * `renameLegacyDraftBranches` renames a legacy draft `vN` → `draft` and leaves its row
 * keyed `…-vN`, so a key built from the name stops matching and the Workflows table opens
 * empty (PSTUDIO-4333). The name is the fallback only for a version with no
 * `botSolutionVersionId` — one created under the new model, whose row uses the uuid anyway.
 *
 * Pass `versionId` when the row is known (the draft, via `pickDraftVersion`): the id is the
 * version's identity, while a name is only a branch name — unique within one solution, which
 * is why a caller holding nothing but a name (`download-version-file`) still resolves.
 */
export const buildVersionPsIdCandidates = ({
  customerId,
  botName,
  versionName,
  versionId,
  solutionVersions,
}: {
  customerId?: string | null;
  botName?: string | null;
  versionName?: string | null;
  versionId?: string | null;
  solutionVersions?: (VersionPsCandidate | null | undefined)[] | null;
}): { updated?: string; legacy?: string } => {
  if (!versionName) {
    return {};
  }

  // `readVersionName`, not `.name`: a legacy version carries `name: null` and keeps its
  // name in `botSolutionVersionId`.
  const version = solutionVersions?.find((candidate) =>
    candidate && versionId
      ? candidate.id === versionId
      : candidate != null && readVersionName(candidate) === versionName
  );

  const legacy = version?.botSolutionVersionId
    ? version.botSolutionVersionId.split('.').join('-')
    : customerId && botName
      ? `${customerId}-${botName}-${versionName}`
      : undefined;

  return {
    updated: version?.id ?? undefined,
    legacy,
  };
};

/**
 * Picks the key that actually addresses the row, given the rows returned for the
 * candidates.
 *
 * `isUsingUpdatedId` is the authority: a row carrying `true` is keyed by the uuid, a row
 * carrying `false` by the legacy composite. When no row exists yet — a version whose Pro
 * Studio row has not been created — the uuid is used, since that is the form every new row
 * gets; the legacy composite is the fallback only when there is no uuid to use.
 */
export const pickVersionPsId = (
  candidates: { updated?: string; legacy?: string },
  rows: (VersionPsRow | null | undefined)[] | null | undefined
): string => {
  const rowFor = (key?: string) =>
    key
      ? (rows?.find((row) => row?.solutionVersionId === key) ?? undefined)
      : undefined;

  const updatedRow = rowFor(candidates.updated);
  if (updatedRow?.isUsingUpdatedId) {
    return candidates.updated ?? '';
  }

  const legacyRow = rowFor(candidates.legacy);
  if (legacyRow) {
    return candidates.legacy ?? '';
  }

  // Neither row exists (or the uuid row exists with the flag still false, which means it
  // has not been migrated to the new key and must not be addressed by uuid).
  if (updatedRow) {
    return candidates.legacy ?? candidates.updated ?? '';
  }

  return candidates.updated ?? candidates.legacy ?? '';
};

/** A legacy composite always ends in `-v<n>`; a uuid never does. */
const LOOKS_LEGACY = /-v\d+$/;

/**
 * Whether a `tenancy_solution_versions_ps` key belongs to the given solution.
 *
 * Used to spot a stale pairing while the user switches solutions — the id and the botId
 * are set together, but a MobX reaction can observe them mid-flight.
 *
 * A legacy composite embeds the solution (`<customer>-<bot>-<version>`, against the
 * DOT-joined `botId`), so a mismatch is detectable. A uuid encodes nothing, so ownership
 * is not checkable and the pairing is trusted — the substring check this replaced could
 * never match a uuid, which silently left the Workflows table empty.
 */
export const versionPsIdBelongsToBot = (
  versionPsId: string | null | undefined,
  botId: string | null | undefined
): boolean => {
  if (!versionPsId) {
    return false;
  }

  if (!botId) {
    return true;
  }

  if (versionPsId.startsWith(`${botId.split('.').join('-')}-`)) {
    return true;
  }

  // A composite that names a DIFFERENT solution is stale; anything else is a uuid.
  return !LOOKS_LEGACY.test(versionPsId);
};

/**
 * The solution a `tenancy_solution_versions_ps` key belongs to, used to tell a publish
 * bump apart from a solution switch on the write path (PSTUDIO-3953).
 *
 * The two key forms have to be answered differently, and only one of them can answer
 * itself:
 *
 *   - **legacy** (`<customer>-<bot>-v<n>`) embeds the solution, so stripping the trailing
 *     `-v<digits>` yields it. That is the dash-joined form `tableStore.botId` already
 *     holds, so both routes agree. Only a segment that LOOKS like a draft version is cut:
 *     cutting at the last dash unconditionally lets `bot=x` + `ver=y-1` collide with
 *     `bot=x-y` + `ver=9`.
 *   - **updated** (the SolutionVersion uuid) encodes nothing — see `versionPsIdBelongsToBot`.
 *     The identity has to come from outside, which is why `solutionId` is passed in.
 *
 * Callers must capture the scope WHEN THE KEY IS SET and compare that against a freshly
 * derived one, never derive both at comparison time: the caller's `solutionId` has already
 * moved to the new solution by then, so two uuids would compare equal and a genuine
 * solution switch would look like a version bump — re-homing one solution's queued rows
 * into another, the exact corruption PSTUDIO-3953 fixed.
 *
 * With no `solutionId` to fall back on, the key itself is returned so that two different
 * uuids compare as different scopes. That errs toward draining the queue (committing to
 * the version being left), which loses nothing, rather than re-homing into the wrong bot,
 * which does.
 */
export const solutionScopeOfVersionPsId = (
  versionPsId: string,
  solutionId: string
): string => {
  if (LOOKS_LEGACY.test(versionPsId)) {
    return versionPsId.replace(LOOKS_LEGACY, '');
  }

  return solutionId || versionPsId;
};
